/**
 * The support-ticket vocabulary. One file, two repos.
 *
 *   SOURCE   downwind-portal/shared/support.ts    ← edit here
 *   MIRROR   downwind-console/shared/support.ts   ← copy, never edit
 *
 * Copied VERBATIM — byte for byte, this header included — and hashed by
 * `support_contract.test.ts` in both repos. Same discipline as
 * `customer_contract.ts`, and needed for a sharper reason than that one: the
 * portal and the console do not talk to each other at all here, they talk to
 * the SAME DATABASE from two Workers. There is no RPC boundary to catch a
 * disagreement, so a status string the console writes and the portal does not
 * understand is a ticket that silently stops rendering.
 *
 * Types and pure constants only. No imports, no DOM, no Workers globals — the
 * SPA bundles this, and so does each Worker.
 *
 * ── The database this describes ─────────────────────────────────────────────
 *
 * Neon project `downwind-support`, a THIRD database, reached through its own
 * `SUPPORT_HYPERDRIVE` binding on both Workers. It is not the licensing
 * database and not the portal's identity database.
 *
 * The cost of that isolation is the thing to understand before writing a query:
 * support rows CANNOT foreign-key to `accounts` (portal DB) or to `licenses`
 * (licensing DB), because those live in other Postgres instances. So the
 * requester's identity and the license a ticket is about are DENORMALIZED onto
 * the ticket at creation. There are no cross-database joins to write, and
 * attempting one is not a slow query — it is an error.
 *
 * What that buys: a support conversation, which contains whatever a customer
 * chose to type and whatever they attached, is backed up, access-controlled and
 * retained on its own terms, separately from the records that decide whether
 * somebody's software runs.
 */

// ─── vocabulary ──────────────────────────────────────────────────────────────

/**
 * The buckets a customer picks on the new-ticket form.
 *
 * Plain text in the schema rather than a Postgres enum, so this list can grow
 * without an `ALTER TYPE` on a table both Workers are reading. Validated in the
 * application, and an unknown value renders verbatim rather than being dropped —
 * the same rule the console applies to audit action codes.
 *
 * Tailored to what this product actually generates. `license` and `devices` are
 * first because between them they are most of the inbox: they are the two
 * things a per-machine license makes people write in about.
 */
export const TICKET_CATEGORIES = [
  "license",
  "devices",
  "purchase",
  "install",
  "bug",
  "other",
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const CATEGORY_LABEL: Readonly<Record<TicketCategory, string>> = {
  license: "License key",
  devices: "Devices and seats",
  purchase: "Purchase or refund",
  install: "Installing or updating",
  bug: "Something is broken",
  other: "Something else",
};

/** What the customer is told the category is for. Shown under each option. */
export const CATEGORY_HINT: Readonly<Record<TicketCategory, string>> = {
  license: "You cannot find your key, or it will not activate.",
  devices: "Out of device slots, or a machine you cannot release.",
  purchase: "Billing, refunds, or a key from a marketplace.",
  install: "The installer or an update will not run.",
  bug: "The product is doing something it should not.",
  other: "Anything that does not fit above.",
};

/**
 * Lifecycle. A customer reply on a finished ticket reopens it to `open` —
 * including a reply that arrives by email — so a customer never has to start
 * over because we closed something too early.
 */
export const TICKET_STATUSES = ["open", "resolved", "closed"] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

/** Operator-set urgency. Never shown to the customer. */
export const TICKET_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

/** Who wrote a message. */
export const MESSAGE_AUTHOR_ROLES = ["customer", "staff"] as const;
export type MessageAuthorRole = (typeof MESSAGE_AUTHOR_ROLES)[number];

/**
 * How a message arrived. Worth recording rather than inferring: a reply that
 * came in by email has been through quoted-history stripping and may be missing
 * context the customer thought they included, and that is the first thing to
 * check when a thread reads oddly.
 */
export const MESSAGE_VIA = ["portal", "email"] as const;
export type MessageVia = (typeof MESSAGE_VIA)[number];

// ─── row shapes ──────────────────────────────────────────────────────────────

/** An image on a message. Bytes live in R2; this is all either UI ever sees. */
export type AttachmentMeta = {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
};

export type TicketMessage = {
  id: string;
  author_role: MessageAuthorRole;
  /** Display name of the author. Null for a customer who set none. */
  author_name: string | null;
  body: string;
  via: MessageVia;
  /** ISO-8601. */
  created_at: string;
  attachments: AttachmentMeta[];
};

/**
 * A ticket as a list row.
 *
 * `account_email` is present because this database cannot join to `accounts`.
 * It is a snapshot taken at creation and is the address the notification mail
 * goes to; if a customer's address were ever moved, existing tickets keep
 * naming the address the conversation actually happened at, which is the honest
 * record.
 */
export type TicketSummary = {
  id: string;
  /** Human-facing running number. Postgres assigns it; the app never writes it. */
  number: number;
  subject: string;
  category: TicketCategory | string;
  status: TicketStatus;
  /** ISO-8601. Bumped by every message; the sort key for both inboxes. */
  last_message_at: string;
  /**
   * `customer` → waiting on us. `staff` → waiting on them. Lets either inbox
   * compute "needs a reply" without touching the messages table.
   */
  last_message_role: MessageAuthorRole;
  /** ISO-8601. */
  created_at: string;
};

/** What the customer sees. No priority, no internal notes, no assignee. */
export type TicketDetail = TicketSummary & {
  messages: TicketMessage[];
  /** The license this ticket was opened about, when it was opened from one. */
  license_key_prefix: string | null;
};

/**
 * What an operator sees. A superset, and deliberately a separate type: adding a
 * column to this one must not be able to leak it to a customer by inheritance.
 */
export type StaffTicketSummary = TicketSummary & {
  account_email: string;
  account_name: string | null;
  priority: TicketPriority;
  /** From the licensing database, denormalized. No join is possible. */
  license_id: string | null;
  license_key_prefix: string | null;
  /** ISO-8601, set once. The first-response clock. */
  first_staff_reply_at: string | null;
};

export type StaffTicketDetail = StaffTicketSummary & {
  messages: StaffTicketMessage[];
  account_id: string;
};

/** Staff see internal notes; `TicketMessage` cannot represent one. */
export type StaffTicketMessage = TicketMessage & {
  /** True for an operator note the customer is never served. */
  internal: boolean;
};

// ─── limits, enforced on both sides of the wire ──────────────────────────────

export const MAX_SUBJECT_LEN = 120;
export const MIN_SUBJECT_LEN = 3;
export const MAX_MESSAGE_LEN = 5000;
export const MIN_MESSAGE_LEN = 2;

/** Per message, mirrored in the client so the form can refuse before uploading. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;
/** 10 MiB decoded, per file. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
export type AllowedImageType = (typeof ALLOWED_IMAGE_TYPES)[number];

/**
 * New tickets per account per hour.
 *
 * Generous, because the failure mode of a tight limit here is a customer who
 * cannot report that they cannot buy the product. The thing actually being
 * prevented is a script, and a script hits ten in a second.
 */
export const TICKETS_PER_HOUR_PER_ACCOUNT = 10;

/** Replies per account per hour, across all their tickets. */
export const REPLIES_PER_HOUR_PER_ACCOUNT = 60;

// ─── helpers ─────────────────────────────────────────────────────────────────

export function isTicketCategory(value: unknown): value is TicketCategory {
  return typeof value === "string" && (TICKET_CATEGORIES as readonly string[]).includes(value);
}

export function isTicketStatus(value: unknown): value is TicketStatus {
  return typeof value === "string" && (TICKET_STATUSES as readonly string[]).includes(value);
}

export function isTicketPriority(value: unknown): value is TicketPriority {
  return typeof value === "string" && (TICKET_PRIORITIES as readonly string[]).includes(value);
}

/** `#1042`. One place, so the console, the portal and the email subject agree. */
export function ticketRef(number: number): string {
  return `#${number}`;
}

/**
 * Whether a ticket is waiting on us.
 *
 * Not simply `status === "open"`: an open ticket whose last message is ours is
 * waiting on the customer, and an operator scanning the inbox for work should
 * not see it. This is the one derived value both inboxes must agree on, which
 * is why it lives here rather than in either UI.
 */
export function needsReply(ticket: {
  status: TicketStatus;
  last_message_role: MessageAuthorRole;
}): boolean {
  return ticket.status === "open" && ticket.last_message_role === "customer";
}
